/**
 * Invariant C — the publish coverage guard (2026-08-07 model spec §3.3).
 *
 * Publish REFUSES when a SKU productDb prices in quarter Q produces no sheet
 * entry for Q and the catalog carries no `unpriced` terminator covering it.
 *
 * WHY THIS EXISTS: priceBookFromEdition strips every pricingQ* / discountQ* key
 * from a governed SKU and restores ONLY what the sheet carries, and
 * getProductPrice (configUtilities.jsx:15) returns 0 for a missing key. A SKU
 * absent from the sheet therefore prices at $0 for every rep, silently — no
 * error, no banner, a real quote. Publish is performed by a catalogAuthor
 * outside any CI or code-review gate, so this is the only place that check can
 * live.
 *
 * WHAT THIS IS NOT: a requirement that a publish touch every SKU. Sparse
 * authoring is the design — a PM updates 9 cells out of 434 and resolveAt
 * carries the rest forward, so an untouched SKU still resolves and still
 * appears in the sheet. What is refused is a SKU that resolves to NOTHING.
 *
 * The deliberate-vs-defect distinction is already in the data: a `kind:
 * 'unpriced'` terminator makes snapshotFromSeed record no cost for that
 * component, which leaves every composition containing it not-fully-costed,
 * which drops the SKU from the sheet on purpose. That is allowed. A missing
 * composition, or one whose components were never costed, is refused.
 *
 * Scope is isGovernedSku — the SAME predicate priceBookFromEdition strips on.
 * A SKU it does not strip keeps its productDb price and cannot be zeroed.
 */
import { resolveAllAt } from './resolveAt';
import { isGovernedSku, storedPriceCents } from './seedBuilder';
import type { CatalogSeed } from './seedBuilder';
import type { PriceSheet } from './editionSheet';
import type { ProductDb } from './priceBookFromEdition';

// 'zero-price' covers any NON-POSITIVE sheet price (<= 0), not only exact
// zero — a negative unit price is strictly worse (it subtracts from the
// customer total) and must refuse the same as $0. Key kept as 'zero-price'
// to avoid rippling the rename through types, tests and the spec.
export type CoverageReason = 'no-composition' | 'uncosted-components' | 'zero-price';

export interface CoverageViolation {
  sku: string;
  quarter: string;
  reason: CoverageReason;
  detail: string;
}

/**
 * A SKU that IS absent from the sheet and was let through, because a terminator
 * covers it. Reported rather than swallowed (spec §3.3): the carve-out matches
 * per COMPONENT, and a component can be shared — `CMP-LABOR-NODE` sits in every
 * node SKU's baseline composition, so one hand-authored `unpriced` row on it
 * excuses every node SKU at once. Narrowing the rule would break a legitimate
 * workflow (retiring a family through a shared component), so the guard
 * discloses its own blast radius instead.
 */
export interface ExcusedSku {
  sku: string;
  quarter: string;
  via: string;      // the terminated componentSku that excused it
}

export interface CoverageReport {
  violations: CoverageViolation[];
  excused: ExcusedSku[];
}

export function checkPublishCoverage(args: {
  sheet: PriceSheet;
  seed: CatalogSeed;
  quarters: string[];
  productDb: ProductDb;
  asOf?: string;
}): CoverageReport {
  const { sheet, seed, quarters, productDb, asOf } = args;

  const compsBySku = new Map<string, CatalogSeed['compositions']>();
  for (const c of seed.compositions) {
    const list = compsBySku.get(c.productSku);
    if (list) list.push(c); else compsBySku.set(c.productSku, [c]);
  }
  const governed = Object.keys(productDb.products).filter(isGovernedSku);

  const violations: CoverageViolation[] = [];
  const excused: ExcusedSku[] = [];
  for (const quarter of [...quarters].sort()) {
    // Same resolution snapshotFromSeed used to build this sheet — same quarter,
    // same asOf. A guard that re-derives its own view can pass while the sheet
    // being published disagrees with it.
    const resolved = resolveAllAt(seed.rows, quarter, asOf);
    const terminated = new Set<string>();
    for (const [componentSku, { row }] of resolved) {
      if (row.kind === 'unpriced') terminated.add(componentSku);
    }

    for (const sku of governed) {
      // Only quarters productDb actually prices are in scope: a SKU that does
      // not exist yet, or whose keys have ended, has no price to lose.
      // storedPriceCents is THE priced predicate (it rounds to cents first and
      // treats an absent or non-numeric key as 0), shared with the terminator
      // pass and the remainder solver so all three reach the same verdict.
      if (storedPriceCents(productDb.products[sku]!, quarter) <= 0) continue;

      const dollars = sheet[sku]?.prices[quarter];

      if (dollars === undefined) {
        const versions = compsBySku.get(sku) ?? [];
        if (versions.length === 0) {
          violations.push({ sku, quarter, reason: 'no-composition',
            detail: 'the catalog has no composition for this SKU' });
          continue;
        }
        // Deliberate retirement: EVERY composition version is knocked out by an
        // explicit terminator. One version terminated while another is merely
        // half-authored is not a retirement — it is a half-authored
        // decomposition, and it refuses. Refusing blocks a publish; allowing
        // ships a $0 price. The asymmetry is the whole point, and the
        // "half-authored second version" case is pinned by a test — swapping
        // this `every` for a `some` must not stay green.
        const excusedVia = versions.map((v) =>
          v.components.find((c) => terminated.has(c.componentSku))?.componentSku);
        if (excusedVia.every((via) => via !== undefined)) {
          // Disclosed, not swallowed (spec §3.3) — see ExcusedSku's doc.
          excused.push({ sku, quarter, via: excusedVia[0]! });
          continue;
        }
        const uncosted = [...new Set(versions
          .flatMap((v) => v.components.map((c) => c.componentSku))
          .filter((componentSku) => !resolved.has(componentSku)))];
        violations.push({ sku, quarter, reason: 'uncosted-components',
          detail: uncosted.length > 0
            ? `no cost this quarter for ${uncosted.join(', ')}`
            : 'no composition version is fully costed this quarter' });
        continue;
      }

      if (dollars <= 0) {
        const cents = storedPriceCents(productDb.products[sku]!, quarter);
        violations.push({ sku, quarter, reason: 'zero-price',
          detail: `the sheet prices it at $${dollars.toFixed(2)} but productDb prices it at ` +
                  `$${(cents / 100).toFixed(2)}` });
      }
    }
  }
  return { violations, excused };
}

const MAX_LISTED = 10;

export function formatCoverageFailure(
  vendor: string, violations: CoverageViolation[],
): string {
  const listed = violations.slice(0, MAX_LISTED)
    .map((v) => `  ${v.sku} @ ${v.quarter} — ${v.detail}`)
    .join('\n');
  const more = violations.length > MAX_LISTED
    ? `\n  …and ${violations.length - MAX_LISTED} more` : '';
  return `Cannot publish ${vendor}: ${violations.length} SKU-quarter ` +
    `${violations.length === 1 ? 'price' : 'prices'} that productDb carries would be ` +
    `zeroed for every rep — this edition governs these SKUs and the sheet does not ` +
    `price them. Fix the catalog data, or retire the SKU deliberately with an ` +
    `'unpriced' row, then publish again.\n${listed}${more}`;
}
